import { ChevronRight, BarChart2 } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { getMasterclassStatsOverviewRoute } from '@/config/routes';
import { useResume } from '@/hooks/useResume';

import { MasterclassProgressBadge } from './MasterclassProgressBadge';

import type { MasterclassCardData } from '../types';

interface MasterclassCardProps {
  masterclass: MasterclassCardData;
}

export const MasterclassCard = ({ masterclass }: MasterclassCardProps) => {
  const { resumeMasterclass } = useResume();
  const navigate = useNavigate();

  // Calculate progress from aggregate session stats (across all case sessions)
  const progress = useMemo(() => {
    const stats = masterclass.mySessionStats;
    const caseCount = masterclass.caseCount || masterclass.patientCases?.length || 0;

    if (!stats) {
      return {
        totalCases: caseCount,
        completedCases: 0,
        isFullyCompleted: false,
        hasStarted: false,
      };
    }

    return {
      totalCases: stats.totalCases || caseCount,
      completedCases: stats.completedCasesCount,
      isFullyCompleted: stats.isFullyCompleted,
      hasStarted: stats.hasStartedAny,
    };
  }, [masterclass.mySessionStats, masterclass.caseCount, masterclass.patientCases?.length]);

  // Button label based on progress
  const buttonLabel = progress.hasStarted ? 'Resume' : 'Start';

  const handleCardClick = () => {
    resumeMasterclass(masterclass.id);
  };

  const handleStatsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    navigate(getMasterclassStatsOverviewRoute(masterclass.id));
  };

  return (
    <Card
      className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
      onClick={handleCardClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xl font-semibold">{masterclass.title}</CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary">{buttonLabel}</span>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <MasterclassProgressBadge
            completedCases={progress.completedCases}
            totalCases={progress.totalCases}
            hasStarted={progress.hasStarted}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleStatsClick}
            className="flex items-center gap-1 border-livelinx-lightgray100 bg-white text-livelinx-purple200 transition-all duration-200 hover:border-livelinx-purple100 hover:bg-livelinx-purple50 hover:text-livelinx-purple200"
          >
            <BarChart2 className="h-4 w-4" />
            <span className="hidden sm:inline">Statistics</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
